import React, { useState } from 'react';
import styles from './styles.css';
import { default as LoadingSpinner } from '../LoadingSpinner/index.jsx';
import {
	ShowClearButtons,
	QuickSelectionButtons,
	MeasureGroupBar,
	SubCategories,
	ExternalMetricsBar,
} from './SubComponents';

import { isSearchedPhrase } from './utils';
import { useTranslation } from 'react-i18next';

const OverallGovernanceBar = ({
	queryParams,
	isSimplifiedView,
	selectedSubjects,
	onSubjectSelect,
	searchPhrase,
	onSubjectChange,
}) => {
	const { t } = useTranslation();
	return (
		<div
			className={`${styles.overallGovernanceBar} ${!isSimplifiedView && selectedSubjects.includes('GOVERNANCE') ? styles.overallGovernanceBarSelected : ''}`}
			style={{ '--selected_color': '52, 60, 66' }}
		>
			<h5 className={`${styles.measureName}`}>
				<a
					href={`/measures/${'GOVERNANCE'.toLowerCase()}.html`}
					className={`${
						isSearchedPhrase({
							subjectName: 'Overall Governance',
							phrase: searchPhrase,
						})
							? styles.highlightSearchedWord
							: ''
					} ${styles.level0}
				${isSimplifiedView ? styles.measureTitle : ''}
				${!isSimplifiedView && selectedSubjects.includes('GOVERNANCE') ? styles.selectedGovernance : ''}
				`}
					onClick={(e) => {
						if (isSimplifiedView && onSubjectChange) {
							e.preventDefault();
							onSubjectChange('GOVERNANCE');
						}
						if (!isSimplifiedView) {
							e.preventDefault();
							onSubjectSelect('GOVERNANCE', queryParams);
						}
					}}
				>
					<img className={styles.measureIcon} src="/img/icons/OG.svg" alt={t('Overall Governance')} />
					{t('Overall Governance')}
					{!isSimplifiedView && selectedSubjects.includes('GOVERNANCE') && (
						<span
							className={styles.deselectX}
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								onSubjectSelect('GOVERNANCE', queryParams);
							}}
						>
							<svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M9 3L3 9M3 3L9 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
							</svg>
						</span>
					)}
				</a>
			</h5>
		</div>
	);
};

const doSearch = ({
	children,
	searchPhrase,
	nestedListsToShow,
	showNestedLists,
	lastSearchPhrase,
	setLastSearchPhrase,
}) => {
	if (searchPhrase !== lastSearchPhrase) {
		setLastSearchPhrase(searchPhrase);
		const ancestors = _doSearch({ children, searchPhrase, ancestors: [] });
		if (JSON.stringify([...ancestors].sort()) !== JSON.stringify([...nestedListsToShow].sort())) {
			showNestedLists(ancestors);
		}
	}
};

const _doSearch = ({ children, searchPhrase, ancestors }) => {
	children.forEach((child) => {
		if (child.childrens) {
			ancestors = _doSearch({ children: child.childrens, searchPhrase, ancestors });
		}

		if (isSearchedPhrase({ subjectName: child.fullName, phrase: searchPhrase })) {
			child.ancestors.forEach((ancestor) => {
				if (!ancestors.includes(ancestor)) {
					ancestors.push(ancestor);
				}
			});
		}
	});
	return ancestors;
};

const SearchInput = ({
	onPhraseChange,
	searchPhrase,
	mainSubjectList,
	nestedListsToShow,
	showNestedLists,
	lastSearchPhrase,
	setLastSearchPhrase,
}) => {
	const { t } = useTranslation();

	return (
		<div className="search-container">
			<input
				onChange={(e) => {
					if (e.target.value.length < 3) {
						showNestedLists([]);
					} else {
						doSearch({
							children: mainSubjectList,
							searchPhrase: e.target.value,
							nestedListsToShow,
							showNestedLists,
							lastSearchPhrase,
							setLastSearchPhrase,
						});
					}
					onPhraseChange(e.target.value);
				}}
				type="text"
				placeholder={t('Search measures')}
				className="search"
				value={searchPhrase || ''}
			/>
			{searchPhrase?.length > 0 ? (
				<span
					onClick={() => {
						showNestedLists([]);
						onPhraseChange('');
					}}
					className="search-icon"
				>
					<svg width="14" height="14" viewBox="0 0 20 20">
						<path fill="none" stroke="currentColor" strokeWidth="2" d="M16 16L4 4M16 4L4 16" />
					</svg>
				</span>
			) : (
				<span className="search-icon">
					<svg width="16" height="16" viewBox="0 0 20 20">
						<circle fill="none" stroke="currentColor" strokeWidth="1.5" cx="9" cy="9" r="7" />
						<path fill="none" stroke="currentColor" strokeWidth="1.5" d="M14 14l5 5" />
					</svg>
				</span>
			)}
		</div>
	);
};

export default function SubjectPickerMenu({
	isSimplifiedView,
	description,
	mainSubjectList,
	show,
	selectedSubjects,
	onSubjectSelect,
	buttons,
	subjectCounters,
	queryParams,
	selectedGroupsOfSubjects,
	onSelectAllSubjects,
	removeSelectedSubject,
	addSelectedSubject,
	selectAllList,
	quickSelectionDescription,
	onPhraseChange,
	searchPhrase,
	onSubjectChange,
	externals,
	selectedExternals,
	onExternalSelect,
}) {
	const { t, i18n } = useTranslation();
	const [nestedListsToShow, showNestedLists] = useState([]);
	const [expandedMeasureBars, setExpandedMeasureBars] = useState([]);
	const [lastSearchPhrase, setLastSearchPhrase] = useState('');

	doSearch({
		children: mainSubjectList,
		searchPhrase,
		nestedListsToShow,
		showNestedLists,
		lastSearchPhrase,
		setLastSearchPhrase,
	});

	return (
		<div
			className={`app-menu measure-menu ${
				isSimplifiedView ? styles.simplifiedSubjectPickerWrapper : styles.subjectPickerWrapper
			} ${show || isSimplifiedView ? '' : styles.hidden}`}
		>
			<div className="icon-container"></div>
			{mainSubjectList.length === 1 ? (
				<LoadingSpinner />
			) : (
				<>
					<div className="app-menu-inner">
						<p className={`menu-description ${isSimplifiedView ? styles.simplifiedViewDescription : ''}`}>
							{description}
						</p>

						<SearchInput
							onPhraseChange={onPhraseChange}
							searchPhrase={searchPhrase}
							mainSubjectList={mainSubjectList}
							showNestedLists={showNestedLists}
							nestedListsToShow={nestedListsToShow}
							lastSearchPhrase={lastSearchPhrase}
							setLastSearchPhrase={setLastSearchPhrase}
						/>

						{!isSimplifiedView ? <ShowClearButtons buttons={buttons} showNestedLists={showNestedLists} /> : null}
						{!isSimplifiedView ? (
							<div className={styles.quickSelectionWrapper}>
								<QuickSelectionButtons
									onSelectAllSubjects={onSelectAllSubjects}
									selectedGroupsOfSubjects={selectedGroupsOfSubjects}
									selectAllList={selectAllList}
									mainSubjectList={mainSubjectList}
									selectedSubjects={selectedSubjects}
									addSelectedSubject={addSelectedSubject}
									quickSelectionDescription={quickSelectionDescription}
									nestedListsToShow={nestedListsToShow}
									showNestedLists={showNestedLists}
								/>
							</div>
						) : null}
						<div className={styles.measuresContainer}>
							<OverallGovernanceBar
								queryParams={queryParams}
								isSimplifiedView={isSimplifiedView}
								selectedSubjects={selectedSubjects}
								onSubjectSelect={onSubjectSelect}
								searchPhrase={searchPhrase}
								onSubjectChange={onSubjectChange}
							/>
							{mainSubjectList.map((subject, index) => (
								<div
									key={`${subject.fullName.split(' ').join('')}_${index}`}
									className={styles.measureGroup}
									style={{ '--selected_color': subject.subjectColor }}
								>
									<MeasureGroupBar
										subject={subject}
										isSimplifiedView={isSimplifiedView}
										selectedSubjects={selectedSubjects}
										onSubjectSelect={onSubjectSelect}
										queryParams={queryParams}
										nestedListsToShow={nestedListsToShow}
										showNestedLists={showNestedLists}
										subjectCounters={subjectCounters}
										selectedGroupsOfSubjects={selectedGroupsOfSubjects}
										onSelectAllSubjects={onSelectAllSubjects}
										addSelectedSubject={addSelectedSubject}
										removeSelectedSubject={removeSelectedSubject}
										expandedMeasureBars={expandedMeasureBars}
										setExpandedMeasureBars={setExpandedMeasureBars}
										searchPhrase={searchPhrase}
										onSubjectChange={onSubjectChange}
										quickSelectionDescription={quickSelectionDescription}
									/>
									{subject?.childrens ? (
										<SubCategories
											isSimplifiedView={isSimplifiedView}
											subjectId={subject.id}
											subCategories={subject.childrens}
											nestedListsToShow={nestedListsToShow}
											showNestedLists={showNestedLists}
											selectedSubjects={selectedSubjects}
											onSubjectSelect={onSubjectSelect}
											subjectColor={subject.subjectColor}
											subjectParent={subject.id}
											subjectType={subject.parent}
											selectedGroupsOfSubjects={selectedGroupsOfSubjects}
											onSelectAllSubjects={onSelectAllSubjects}
											removeSelectedSubject={removeSelectedSubject}
											addSelectedSubject={addSelectedSubject}
											queryParams={queryParams}
											searchPhrase={searchPhrase}
											onSubjectChange={onSubjectChange}
										/>
									) : null}
								</div>
							))}
							{externals && externals.length > 0 && (
								<div key={`external_data_picker`}>
									<ExternalMetricsBar
										externals={externals}
										selectedExternals={selectedExternals}
										onExternalSelect={onExternalSelect}
										// subject={subject}
										// isSimplifiedView={isSimplifiedView}
										// selectedSubjects={selectedSubjects}
										// onSubjectSelect={onSubjectSelect}
										// queryParams={queryParams}
										nestedListsToShow={nestedListsToShow}
										showNestedLists={showNestedLists}
										// subjectCounters={subjectCounters}
										// selectedGroupsOfSubjects={selectedGroupsOfSubjects}
										// onSelectAllSubjects={onSelectAllSubjects}
										// addSelectedSubject={addSelectedSubject}
										// removeSelectedSubject={removeSelectedSubject}
										// expandedMeasureBars={expandedMeasureBars}
										// setExpandedMeasureBars={setExpandedMeasureBars}
										// searchPhrase={searchPhrase}
										// onSubjectChange={onSubjectChange}
									/>
								</div>
							)}
						</div>
					</div>
				</>
			)}
		</div>
	);
}
